import { Injectable } from '@angular/core';
import { CategoryQuery, HydratedSyllabusTopicsTree } from '@examdojo/category/v2';
import { distinctUntilArrayChanged, shareOneReplay } from '@examdojo/rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, debounceTime, map } from 'rxjs';
import { AssetTreeNode, AssetTreeNodeType } from '../../model/asset-tree-node.model';
import { getAssetPath, getUniqueModelName, getUniqueUIName } from './asset-tree-builder.utils';
import { findInTree } from './asset-tree.utils';

@UntilDestroy()
@Injectable()
export class AssetTreeBuilderService {
  constructor(private readonly query: CategoryQuery) {}

  private readonly temporaryDirectories$$ = new BehaviorSubject<string[]>([]);
  readonly temporaryDirectories$ = this.temporaryDirectories$$.asObservable();

  private readonly defaultTree$ = combineLatest([
    this.query.activeSyllabusTopics$,
    this.temporaryDirectories$$.pipe(distinctUntilArrayChanged({ orderMatters: false })),
    // this.query.selectStarredUIEntities([...topLevelEntityTypes, ...childLevelEntityTypes]).pipe(startWith([])), TODO: favorites
  ]).pipe(
    // Debounce prevents rerendering of the tree during the heavy move operations.
    debounceTime(25),
    map(([entities, temporaryDirectories]) => this.createAssetTree(entities, { temporaryDirectories })),
    shareOneReplay(),
  );

  readonly rootNode$ = this.defaultTree$;

  readonly sourceDirectoryNode$ = this.rootNode$.pipe(
    map((tree) => findInTree(tree, (node) => node.nodeType === AssetTreeNodeType.SourceDirectory)),
    shareOneReplay(),
  );

  readonly assetsContainerNode$ = this.rootNode$.pipe(
    map((tree) => findInTree(tree, (node) => node.nodeType === AssetTreeNodeType.AssetsContainer)),
    shareOneReplay(),
  );

  getTemporaryDirectories() {
    return this.temporaryDirectories$$.value;
  }

  setTemporaryDirectories(temporaryDirectories: string[]) {
    this.temporaryDirectories$$.next(temporaryDirectories);
  }

  clearTemporaryDirectories() {
    this.temporaryDirectories$$.next([]);
  }

  createRootNode(): AssetTreeNode {
    return {
      nodeType: AssetTreeNodeType.Root,
      uiName: 'ROOT',
      modelName: 'ROOT',
      path: 'ROOT',
      children: {},
    };
  }

  getAssetsContainerNode(rootNode: AssetTreeNode): AssetTreeNode {
    return {
      nodeType: AssetTreeNodeType.AssetsContainer,
      modelName: 'assets',
      uiName: 'examdojo.learn_hub.overview',
      path: `${rootNode.path}/assets`,
      parent: rootNode,
      children: {},
      order: 1,
    };
  }

  private createAssetTree(
    assets: HydratedSyllabusTopicsTree,
    options: {
      temporaryDirectories?: string[];
      starredEntities?: HydratedSyllabusTopicsTree;
    } = {},
  ): AssetTreeNode {
    const root = this.createRootNode();

    if (options.starredEntities && options.starredEntities.length > 0) {
      const favNode: AssetTreeNode = (root.children['favorites'] = {
        nodeType: AssetTreeNodeType.FavoritesContainer,
        uiName: 'Favorites',
        modelName: 'favorites',
        path: `${root.path}/favorites`,
        parent: root,
        order: 0,
        children: {},
      });

      for (const asset of options.starredEntities) {
        const uniqueModelName = getUniqueModelName(asset);
        const uniqueUIName = getUniqueUIName(asset);

        favNode.children[uniqueModelName] = {
          nodeType: AssetTreeNodeType.FavoriteAsset,
          modelName: uniqueModelName,
          uiName: uniqueUIName,
          asset,
          parent: favNode,
          path: `${favNode.path}/${uniqueModelName}`,
          children: {},
        };
      }
    }

    if (!assets.length && !options.temporaryDirectories?.length) {
      return root;
    }

    const assetsContainer = this.getAssetsContainerNode(root);

    root.children['assets'] = assetsContainer;

    for (const topicLevel1 of assets) {
      const assetPath = getAssetPath(topicLevel1);
      const topicLevel1Node: AssetTreeNode = {
        nodeType: AssetTreeNodeType.TopicLevel1,
        modelName: getUniqueModelName(topicLevel1),
        uiName: getUniqueUIName(topicLevel1),
        path: assetPath,
        asset: topicLevel1,
        parent: assetsContainer,
        order: topicLevel1.sort_order,
        children: {},
      };

      assetsContainer.children[assetPath] = {
        ...topicLevel1Node,
        children: topicLevel1.children.reduce<AssetTreeNode['children']>(
          (acc, topicLevel2) => {
            const path = getAssetPath(topicLevel2);
            const node: AssetTreeNode = {
              nodeType: AssetTreeNodeType.TopicLevel2,
              modelName: getUniqueModelName(topicLevel2),
              uiName: getUniqueUIName(topicLevel2),
              path,
              asset: topicLevel2,
              parent: topicLevel1Node,
              order: topicLevel2.sort_order,
              children: {},
            };
            return { ...acc, [path]: node };
          },
          {} as AssetTreeNode['children'],
        ),
      } satisfies AssetTreeNode;
    }

    for (const tempDir of options.temporaryDirectories ?? []) {
      const pathParts = tempDir.split('/').slice(1);

      let node = root;

      for (const pathPart of pathParts) {
        let child = node.children[pathPart];

        if (!child) {
          const parentPath = node.path ? `${node.path}/` : '';

          child = node.children[pathPart] = {
            nodeType: AssetTreeNodeType.TemporaryDirectory,
            modelName: pathPart,
            uiName: pathPart,
            parent: node,
            path: `${parentPath}${pathPart}`,
            children: {},
          };
        }

        node = child;
      }
    }

    return root;
  }
}
